import pptxgen from "pptxgenjs";
import type { Offer, OfferLine } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";
import {
  calcOfferTotals,
  mergeOfferWritingForExport,
} from "@/lib/offer-calc";
import { isLineVisible } from "@/lib/offer-budget-image-geometry";
import {
  ASTERISK_ROSA_SVG,
  FOX_WHITE_SVG,
  OFFER_EXPORT_ASTERISK_IN,
  OFFER_EXPORT_MARK_AXIS_IN,
  OFFER_EXPORT_COLORS,
  OFFER_EXPORT_FONT_BODY,
  OFFER_EXPORT_FONT_DISPLAY,
  OFFER_EXPORT_LEFT_TEXT_MIN_TOP_IN,
  OFFER_EXPORT_VERTICAL_HEAD_Y_IN,
  svgDataUrl,
} from "@/lib/offer-export-brand";
import {
  estimateBudgetLinesHeight,
  paginateBudgetLines,
  pptxColumnWidths,
  PPTX_CELL_MARGIN_IN,
  PPTX_COMPACT_TABLE_IN,
} from "@/lib/offer-export-paginate";
import {
  buildBudgetTableRows,
  type BudgetTableRow,
} from "@/lib/offer-export-table";
import { buildLeftPanelBlocks } from "@/lib/offer-export-terms";

interface SectionGroup {
  section: OfferSection;
  lines: OfferLine[];
  subtotal: number;
}

interface SlideRenderOptions {
  offer: Offer;
  visibleLines: OfferLine[];
  totals: ReturnType<typeof calcOfferTotals>;
  fontSizeRow: number;
  fontSizePhase: number;
  rowH: number;
  isLastPage: boolean;
  pageNumber: number;
  pageTotal: number;
  /**
   * Vannrett overskrift over prosjektnavnet. `null` for hovedbudsjettet:
   * den roterte «budsjett_» sier allerede hva sliden er, så en «Budsjett_»
   * til var bare støy (Erik 29.09). Tilvalg-slidene beholder sin, fordi
   * navnet på tilvalget ikke står noe annet sted på sliden.
   */
  slideTitle: string | null;
  hideOverheadAndDiscount?: boolean;
  customTermsTextOverride?: string | null;
}

const NO_BORDER = { pt: 0, color: OFFER_EXPORT_COLORS.tableBg } as const;

const SLIDE_H = 7.5;

/**
 * Høyden en venstrepanel-blokk vil ta. Høydegjetningen var kalibrert for
 * 12 pt; på 15 pt i en 4,17" bred boks får det plass ~38 tegn per linje, og
 * hver linje er ~0,26" høy.
 *
 * Den må kunne kalles FØR vi tegner, fordi blokken nå sentreres vertikalt og
 * plasseringen derfor avhenger av den samlede høyden.
 */
function leftBlockHeight(block: { key: string; text: string }): number {
  const lineGuess = Math.max(1, Math.ceil(block.text.length / 38));
  return block.key === "body"
    ? Math.min(2.6, Math.max(1.0, lineGuess * 0.26))
    : Math.min(0.9, Math.max(0.36, lineGuess * 0.26));
}

/**
 * Samme anslag for overskriftene over brødteksten (prosjektnavnet og
 * «Tilvalg: …»).
 *
 * De sto på en fast 0,34–0,42" og ble alltid flyttet 0,4" ned, altså som om
 * de var én linje. Et prosjektnavn som brekker over flere linjer rant derfor
 * rett ned i brødteksten under: `h` styrer ikke hvor mye tekst Keynote
 * tegner, bare hvor mye plass VI har regnet med. Sett med 127 tegn i
 * «Kombinert: Eksisterende materiale: …» (Erik 29.09), der navnet tok fire
 * linjer og overlappet standardteksten.
 *
 * Taket er en sikkerhetsventil: et absurd langt navn skal ikke kunne skyve
 * resten av panelet av sliden.
 */
function leftHeadingHeight(text: string, minH: number): number {
  const lineGuess = Math.max(1, Math.ceil(text.length / 38));
  return Math.min(1.56, Math.max(minH, lineGuess * 0.26));
}

/** Luft under en overskrift. Én linje gir 0,34 + 0,06 = 0,4" som før. */
const HEADING_GAP = 0.06;

function toPptxRows(rows: BudgetTableRow[]): pptxgen.TableRow[] {
  return rows.map((row) =>
    row.map((c) => ({
      text: c.text,
      options: {
        colspan: c.colspan,
        align: c.align,
        bold: c.bold,
        italic: c.italic,
        color: c.color,
        fontFace: c.fontFace,
        fontSize: c.fontSize,
        valign: "middle" as const,
        fill: { color: OFFER_EXPORT_COLORS.tableBg },
        // pptxgenjs vil ha kantene i rekkefølgen topp, høyre, bunn, venstre.
        // Sidene er alltid usynlige — vi tegner kun vannrette skillelinjer.
        border: [
          c.borderTop ?? NO_BORDER,
          NO_BORDER,
          c.borderBottom ?? NO_BORDER,
          NO_BORDER,
        ],
        margin: [
          PPTX_CELL_MARGIN_IN,
          PPTX_CELL_MARGIN_IN,
          PPTX_CELL_MARGIN_IN,
          PPTX_CELL_MARGIN_IN,
        ],
      },
    })),
  );
}

function renderBudgetSlide(pptx: pptxgen, opts: SlideRenderOptions): void {
  const {
    offer,
    visibleLines,
    totals,
    fontSizeRow,
    fontSizePhase,
    rowH,
    isLastPage,
    pageNumber,
    pageTotal,
    slideTitle,
    hideOverheadAndDiscount = false,
    customTermsTextOverride,
  } = opts;

  const slide = pptx.addSlide();

  // VIB-2556: malen deler sliden på midten (13,44" av 26,67" → 50,4 %).
  const LEFT_W = 6.72;
  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: LEFT_W,
    h: 7.5,
    fill: { color: OFFER_EXPORT_COLORS.dark },
    line: { color: OFFER_EXPORT_COLORS.dark, width: 0 },
  });

  slide.addImage({
    data: svgDataUrl(ASTERISK_ROSA_SVG),
    // Erik 29.09: var 0,44" og dominerte hjørnet. Merket sentreres PÅ
    // AKSEN i stedet for å ha hardkodet venstrekant — da det ble krympet
    // med venstrekanten i behold gled senteret 0,22" til venstre, og
    // flukten med den roterte «budsjett_» røk.
    x: OFFER_EXPORT_MARK_AXIS_IN - OFFER_EXPORT_ASTERISK_IN / 2,
    y: 0.665,
    w: OFFER_EXPORT_ASTERISK_IN,
    h: OFFER_EXPORT_ASTERISK_IN,
  });

  // Malen setter «budsjett_» i 50 pt på sitt dobbelt store lerret → 25 pt
  // hos oss, i brødtekstfonten, og på linje med asterisken (senter x 1,11").
  slide.addText("budsjett_", {
    // Boksen er 2,8" bred og roteres 270°, så den sentreres om x + w/2 —
    // altså om den samme aksen asterisken står på.
    x: OFFER_EXPORT_MARK_AXIS_IN - 1.4,
    y: OFFER_EXPORT_VERTICAL_HEAD_Y_IN,
    w: 2.8,
    h: 0.42,
    rotate: 270,
    fontSize: 25,
    color: OFFER_EXPORT_COLORS.darkText,
    fontFace: OFFER_EXPORT_FONT_BODY,
    align: "center" as const,
    margin: 0,
  });

  const leftX = 1.815;
  const leftW = 4.17;

  const customTermsText =
    customTermsTextOverride !== undefined
      ? customTermsTextOverride ?? ""
      : offer.custom_terms_text ?? "";
  const blocks = buildLeftPanelBlocks(offer, customTermsText);

  // Erik 29.09: malens y 0,425" la teksten helt oppe i hjørnet, med et tomt
  // bunnfelt under. Blokken måles derfor opp først og sentreres i panelet,
  // slik malen gjør med sin lengre vilkårstekst. Gulvet gjør at en veldig
  // lang tekst vokser nedover i stedet for å klatre opp forbi asterisken.
  const slideTitleH = slideTitle ? leftHeadingHeight(slideTitle, 0.34) : 0;
  const offerTitleH = offer.title ? leftHeadingHeight(offer.title, 0.34) : 0;
  const contentH =
    (slideTitle ? slideTitleH + HEADING_GAP : 0) +
    (offer.title ? offerTitleH + HEADING_GAP : 0) +
    (pageTotal > 1 ? 0.3 : 0) +
    blocks.reduce((sum, b) => sum + leftBlockHeight(b) + 0.1, 0);
  let leftY = Math.max(
    OFFER_EXPORT_LEFT_TEXT_MIN_TOP_IN,
    (SLIDE_H - contentH) / 2,
  );

  if (slideTitle) {
    const titleFontSize = slideTitle.length > 18 ? 14 : 16;
    slide.addText(slideTitle, {
      x: leftX,
      y: leftY,
      w: leftW,
      h: slideTitleH,
      fontSize: titleFontSize,
      color: OFFER_EXPORT_COLORS.darkText,
      fontFace: OFFER_EXPORT_FONT_DISPLAY,
      margin: 0,
    });
    leftY += slideTitleH + HEADING_GAP;
  }

  if (offer.title) {
    slide.addText(offer.title, {
      x: leftX,
      y: leftY,
      w: leftW,
      h: offerTitleH,
      fontSize: 15,
      color: OFFER_EXPORT_COLORS.rosa,
      fontFace: OFFER_EXPORT_FONT_DISPLAY,
      margin: 0,
    });
    leftY += offerTitleH + HEADING_GAP;
  }

  if (pageTotal > 1) {
    slide.addText(`Side ${pageNumber} av ${pageTotal}`, {
      x: leftX,
      y: leftY,
      w: leftW,
      h: 0.26,
      fontSize: 11,
      color: OFFER_EXPORT_COLORS.mutedText,
      fontFace: OFFER_EXPORT_FONT_BODY,
      margin: 0,
    });
    leftY += 0.3;
  }

  for (const block of blocks) {
    const h = leftBlockHeight(block);
    slide.addText(block.text, {
      x: leftX,
      y: leftY,
      w: leftW,
      h,
      fontSize: 15,
      // Halvfet er `bold` på familien, ikke et eget familienavn: Semibold
      // er en STIL inni «Clash Grotesk» (se fontkommentaren i brand).
      bold: block.bold,
      color: OFFER_EXPORT_COLORS.darkText,
      fontFace: OFFER_EXPORT_FONT_BODY,
      valign: "top" as const,
      margin: 0,
    });
    leftY += h + 0.1;
  }

  const PANEL_X = LEFT_W;
  const PANEL_Y = 0;
  const PANEL_W = 13.333 - LEFT_W;
  const PANEL_H = 7.5;

  slide.addShape("rect", {
    x: PANEL_X,
    y: PANEL_Y,
    w: PANEL_W,
    h: PANEL_H,
    fill: { color: OFFER_EXPORT_COLORS.tableBg },
    line: { color: OFFER_EXPORT_COLORS.tableBg, width: 0 },
  });

  const FOX = 0.5;
  slide.addShape("ellipse", {
    x: PANEL_X + PANEL_W - FOX - 0.32,
    y: PANEL_H - FOX - 0.32,
    w: FOX,
    h: FOX,
    fill: { color: OFFER_EXPORT_COLORS.reveblaa },
    line: { color: OFFER_EXPORT_COLORS.reveblaa, width: 0 },
  });
  const foxInner = FOX * 0.72;
  const foxPad = (FOX - foxInner) / 2;
  slide.addImage({
    data: svgDataUrl(FOX_WHITE_SVG),
    x: PANEL_X + PANEL_W - FOX - 0.32 + foxPad,
    y: PANEL_H - FOX - 0.32 + foxPad,
    w: foxInner,
    h: foxInner,
  });

  // Malens tabell: x 16,33" → 8,165", y 1,51" → 0,755", bredde 7,73" → 3,865".
  // Bredden er utvidet til PPTX_COMPACT_TABLE_IN, og tabellen sentreres i det
  // grå feltet i stedet for å stå på malens x: malen hadde 1,45" tomt til
  // venstre og 1,30" til høyre, og skjevheten blir tydelig når tabellen vokser.
  const PAD_Y = 0.755;
  const TABLE_W = PPTX_COMPACT_TABLE_IN;
  const TABLE_X = PANEL_X + (PANEL_W - TABLE_W) / 2;
  const tableStartY = PANEL_Y + PAD_Y;
  const discountPctText =
    offer.discount_amount > 0
      ? ""
      : offer.discount_pct > 0
        ? `-${offer.discount_pct} %`
        : "";

  const addNativeTable = (
    rows: BudgetTableRow[],
    x: number,
    y: number,
    width: number,
  ) => {
    if (rows.length === 0) return;
    // Malens kolonnefordeling, utvidet hvis beløpskolonnen ellers ville
    // brukket et tall over to linjer (SUM-raden er størst og i bold).
    const colW = pptxColumnWidths(
      width,
      rows
        .map((r) => r[r.length - 1])
        .filter((c) => c && c.align === "right")
        .map((c) => ({
          text: c.text,
          fontSize: c.fontSize ?? fontSizeRow,
          bold: c.bold,
        })),
    );
    slide.addTable(toPptxRows(rows), {
      x,
      y,
      w: width,
      colW,
      fontFace: OFFER_EXPORT_FONT_BODY,
      fontSize: fontSizeRow,
      color: OFFER_EXPORT_COLORS.tableText,
      fill: { color: OFFER_EXPORT_COLORS.tableBg },
      border: { pt: 0, color: OFFER_EXPORT_COLORS.tableBg },
      valign: "middle",
      rowH,
    });
  };

  addNativeTable(
    buildBudgetTableRows({
      visibleLines,
      fontSizeRow,
      fontSizePhase,
      includeTotals: isLastPage,
      hideOverheadAndDiscount,
      totals,
      overheadPct: offer.overhead_pct,
      discountPctText,
    }),
    TABLE_X,
    tableStartY,
    TABLE_W,
  );
}

function pickReadableFont(
  visibleLines: OfferLine[],
  totalsRows: number,
  availableH: number,
) {
  const steps = [
    // VIB-2556: malens radhøyde er 0,356" av dens 15"-lerret → 0,178" hos
    // oss, og fase-headeren bare litt høyere (0,376–0,411" → ~0,19").
    // De gamle 0,24–0,28" gjorde tabellen 35–57 % luftigere enn malen.
    // Første trinn ER malens: 8 pt i 0,178" rad, 0,0278" cellemargin. Det
    // gir 8,8 pt til en 8 pt linje — 1,10 × fontstørrelsen, samme forhold
    // som malen. De neste trinnene holder det forholdet når budsjettet er
    // større enn malens, i stedet for å klemme teksten.
    { row: 8, phase: 9, rowH: 0.178, phaseH: 0.2 },
    { row: 7, phase: 8, rowH: 0.165, phaseH: 0.185 },
    { row: 6, phase: 7, rowH: 0.15, phaseH: 0.17 },
  ];
  for (const step of steps) {
    const linesH = estimateBudgetLinesHeight(
      visibleLines,
      step.rowH,
      step.phaseH,
    );
    const totalsH = totalsRows * step.rowH + 0.15;
    if (linesH + totalsH <= availableH) {
      return step;
    }
  }
  return steps[1];
}

function renderSectionSlides(
  pptx: pptxgen,
  opts: {
    offer: Offer;
    visibleLines: OfferLine[];
    totals: ReturnType<typeof calcOfferTotals>;
    slideTitle: string | null;
    hideOverheadAndDiscount: boolean;
    customTermsTextOverride?: string | null;
  },
): number {
  const {
    offer,
    visibleLines,
    totals,
    slideTitle,
    hideOverheadAndDiscount,
    customTermsTextOverride,
  } = opts;

  const PANEL_H = 7.5;
  // VIB-2556: malens tabell er 12,00" av dens 15"-lerret → 6,00" hos oss,
  // fra y 0,755 til 6,755. Den gamle beregningen (0,67 padding + 0,7
  // reservert til rev-badgen) ga bare 5,46" og delte budsjettet i to
  // slides unødig. Badgen ligger helt til høyre (x 12,51), utenfor
  // tabellens 8,16–12,03, så den trenger ingen egen høydereserve.
  const AVAILABLE_LINES_H = 6.0;

  const totalsRows =
    (hideOverheadAndDiscount ? 0 : totals.overhead_amount > 0 ? 1 : 0) +
    (hideOverheadAndDiscount ? 0 : totals.discount_amount > 0 ? 1 : 0) +
    1;

  const chosen = pickReadableFont(visibleLines, totalsRows, AVAILABLE_LINES_H);
  const lastStepTotalsH = totalsRows * chosen.rowH + 0.15;
  const maxLinesHPerPage = AVAILABLE_LINES_H - lastStepTotalsH;
  const linesH = estimateBudgetLinesHeight(
    visibleLines,
    chosen.rowH,
    chosen.phaseH,
  );
  const fitsOnOnePage = linesH <= maxLinesHPerPage;

  const pages: OfferLine[][] = fitsOnOnePage
    ? [visibleLines]
    : paginateBudgetLines(
        visibleLines,
        chosen.rowH,
        chosen.phaseH,
        maxLinesHPerPage,
      );

  pages.forEach((pageLines, idx) => {
    const isLast = idx === pages.length - 1;
    renderBudgetSlide(pptx, {
      offer,
      visibleLines: pageLines,
      totals,
      fontSizeRow: chosen.row,
      fontSizePhase: chosen.phase,
      rowH: chosen.rowH,
      isLastPage: isLast,
      pageNumber: idx + 1,
      pageTotal: pages.length,
      slideTitle,
      hideOverheadAndDiscount,
      customTermsTextOverride,
    });
  });

  return pages.length;
}

/**
 * VIB-2091: Keynote-nedlasting. Filen er PPTX (Keynote åpner den nativt) med
 * én native tabell per lysbilde, merkevare-komposisjon (asterisk, rosa
 * undertittel, full-bleed Revegrå, rev i sirkel) og Clash-fonter. Hovedtilbud
 * på første slide, hvert tilvalg på egne slides (VIB-663e).
 */
export async function exportOfferAsPptx(
  offer: Offer,
  lines: OfferLine[],
  sections: OfferSection[] = [],
) {
  const exportLines = mergeOfferWritingForExport(lines);
  const visibleLines = exportLines.filter(isLineVisible);

  const mainVisibleLines = visibleLines.filter((l) => !l.section_id);
  const sectionGroups: SectionGroup[] = sections
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((s) => ({
      section: s,
      lines: visibleLines.filter((l) => l.section_id === s.id),
      subtotal: visibleLines
        .filter((l) => l.section_id === s.id)
        .reduce((sum, l) => sum + (l.amount ?? 0), 0),
    }))
    .filter((g) => g.lines.length > 0);

  const pptx = new pptxgen();
  pptx.defineLayout({ name: "SOLVREV_16_9", width: 13.333, height: 7.5 });
  pptx.layout = "SOLVREV_16_9";
  pptx.author = "Sølvrev";
  pptx.company = "Sølvrev AS";
  pptx.title = offer.title;

  if (mainVisibleLines.length > 0) {
    const mainLines = lines.filter((l) => !l.section_id);
    const mainTotals = calcOfferTotals(
      {
        overhead_pct: offer.overhead_pct,
        discount_pct: offer.discount_pct,
        discount_amount: offer.discount_amount,
        include_travel_in_overhead: offer.include_travel_in_overhead,
      },
      mainLines.map((l) => ({
        phase: l.phase,
        cost_type: l.cost_type,
        pricing_type: l.pricing_type,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
        purchase_price: l.purchase_price,
        amount: l.amount,
        overtime_split: l.overtime_split ?? null,
      })),
    );
    renderSectionSlides(pptx, {
      offer,
      visibleLines: mainVisibleLines,
      totals: mainTotals,
      // Ingen vannrett overskrift: den roterte «budsjett_» dekker det.
      slideTitle: null,
      hideOverheadAndDiscount: false,
    });
  }

  for (const group of sectionGroups) {
    const groupRawLines = lines.filter((l) => l.section_id === group.section.id);
    const groupTotals = calcOfferTotals(
      {
        overhead_pct: offer.overhead_pct,
        discount_pct: offer.discount_pct,
        discount_amount: 0,
        include_travel_in_overhead: offer.include_travel_in_overhead,
      },
      groupRawLines.map((l) => ({
        phase: l.phase,
        cost_type: l.cost_type,
        pricing_type: l.pricing_type,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
        purchase_price: l.purchase_price,
        amount: l.amount,
        overtime_split: l.overtime_split ?? null,
      })),
    );
    renderSectionSlides(pptx, {
      offer,
      visibleLines: group.lines,
      totals: groupTotals,
      slideTitle: `Tilvalg: ${group.section.name}`,
      hideOverheadAndDiscount: false,
      customTermsTextOverride: group.section.custom_terms_text,
    });
  }

  if (mainVisibleLines.length === 0 && sectionGroups.length === 0) {
    renderSectionSlides(pptx, {
      offer,
      visibleLines: [],
      totals: calcOfferTotals(
        {
          overhead_pct: offer.overhead_pct,
          discount_pct: offer.discount_pct,
          discount_amount: offer.discount_amount,
          include_travel_in_overhead: offer.include_travel_in_overhead,
        },
        [],
      ),
      // Ingen vannrett overskrift: den roterte «budsjett_» dekker det.
      slideTitle: null,
      hideOverheadAndDiscount: false,
    });
  }

  const filename = `Tilbud_${offer.title.replace(/[^a-zA-Z0-9_-]+/g, "_")}.pptx`;
  await pptx.writeFile({ fileName: filename });
}
